/**
 * Phase 7 Slice B — ConnectionEditor page.
 *
 * Operator-facing detail editor for a CRM connection. Lives at
 * /app/integrations/crm/connections/:id. See ADR-0034.
 *
 * The OAuth tokens and webhook secret are write-only: the GET response
 * already strips the `*_enc` fields and exposes `oauth_configured: bool`
 * and `webhook_secret_configured: bool`. Rotation opens a modal whose
 * fields never round-trip — submit → PATCH → server encrypts → response
 * strips back to booleans.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AuditHistoryLink from "../../components/app/AuditHistoryLink";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plug, KeyRound, Webhook, RefreshCw, FlaskConical, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";

const STATUS_BADGE = {
  connected: "bg-emerald-100 text-emerald-700",
  disconnected: "bg-muted text-muted-foreground",
  error: "bg-rose-100 text-rose-700",
  test_only: "bg-amber-100 text-amber-700",
};

function StatusBadge({ status }) {
  const cls = STATUS_BADGE[status] || STATUS_BADGE.disconnected;
  return <Badge className={cls} data-testid={`connection-status-${status}`}>
    {status}
  </Badge>;
}

function OAuthRotateDialog({ open, onClose, onSubmit }) {
  const t = useT();
  const [form, setForm] = useState({ access_token: "", refresh_token: "",
                                          expires_at: "" });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = () => {
    if (!form.access_token) return;
    onSubmit({
      access_token: form.access_token,
      refresh_token: form.refresh_token || null,
      expires_at: form.expires_at || null,
    });
    setForm({ access_token: "", refresh_token: "", expires_at: "" });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="oauth-rotate-dialog">
        <DialogHeader>
          <DialogTitle>
            <KeyRound className="h-4 w-4 inline mr-1" />
            {t("connection_detail.rotate_oauth", "OAuth-Tokens rotieren")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>access_token *</Label>
            <Input type="password" value={form.access_token}
                   onChange={(e) => set("access_token", e.target.value)}
                   data-testid="oauth-access-token" />
          </div>
          <div>
            <Label>refresh_token</Label>
            <Input type="password" value={form.refresh_token}
                   onChange={(e) => set("refresh_token", e.target.value)}
                   data-testid="oauth-refresh-token" />
          </div>
          <div>
            <Label>expires_at (ISO 8601)</Label>
            <Input placeholder="2026-12-31T23:59:59Z" value={form.expires_at}
                   onChange={(e) => set("expires_at", e.target.value)}
                   data-testid="oauth-expires-at" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                  data-testid="oauth-rotate-cancel">
            {t("common.cancel", "Abbrechen")}
          </Button>
          <Button disabled={!form.access_token} onClick={submit}
                  data-testid="oauth-rotate-submit">
            {t("connection_detail.rotate", "Rotieren")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SecretRotateDialog({ open, onClose, onSubmit }) {
  const t = useT();
  const [secret, setSecret] = useState("");
  const submit = () => { if (secret) { onSubmit(secret); setSecret(""); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="secret-rotate-dialog">
        <DialogHeader>
          <DialogTitle>
            <Webhook className="h-4 w-4 inline mr-1" />
            {t("connection_detail.rotate_secret", "Webhook-Secret rotieren")}
          </DialogTitle>
        </DialogHeader>
        <div>
          <Label>webhook_secret *</Label>
          <Input type="password" value={secret}
                 onChange={(e) => setSecret(e.target.value)}
                 data-testid="secret-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                  data-testid="secret-rotate-cancel">
            {t("common.cancel", "Abbrechen")}
          </Button>
          <Button disabled={!secret} onClick={submit}
                  data-testid="secret-rotate-submit">
            {t("connection_detail.rotate", "Rotieren")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ConnectionEditor() {
  const { id } = useParams();
  const t = useT();
  const qc = useQueryClient();
  const [edit, setEdit] = useState({
    name: undefined, status: undefined, config: undefined,
  });
  const [configError, setConfigError] = useState(null);
  const [oauthOpen, setOauthOpen] = useState(false);
  const [secretOpen, setSecretOpen] = useState(false);
  const [banner, setBanner] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["crm-connection", id],
    queryFn: async () => {
      const r = await api.get(`/integrations/crm/connections/${id}`);
      return r.data;
    },
  });

  const patch = useMutation({
    mutationFn: async (body) => {
      const r = await api.patch(`/integrations/crm/connections/${id}`, body);
      return r.data;
    },
    onSuccess: (out) => {
      qc.setQueryData(["crm-connection", id], out);
      toast.success(t("connection_detail.saved", "Gespeichert."));
    },
    onError: () => toast.error(t("connection_detail.save_error",
        "Speichern fehlgeschlagen.")),
  });

  const test = useMutation({
    mutationFn: async () => {
      const r = await api.post(`/integrations/crm/connections/${id}/test`);
      return r.data;
    },
    onSuccess: (r) => setBanner({ kind: "test", ok: r.ok !== false,
        message: r.message || JSON.stringify(r) }),
    onError: (e) => setBanner({ kind: "test", ok: false,
        message: e?.response?.data?.detail || "test_failed" }),
  });

  const refreshTokens = useMutation({
    mutationFn: async ({ failureSimulation = false } = {}) => {
      const r = await api.post(
        `/integrations/crm/connections/${id}/refresh-tokens`,
        null,
        failureSimulation
          ? { headers: { "X-Mock-Refresh-Failure": "true" } }
          : undefined,
      );
      return r.data;
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["crm-connection", id] });
      toast.success(t("connection_detail.refresh_ok",
                            "OAuth-Token aktualisiert."));
      setBanner({ kind: "refresh", ok: true,
                     message: `expires_at = ${r.expires_at}` });
    },
    onError: (e) => {
      const reason = e?.response?.data?.detail?.reason
        || e?.response?.data?.detail
        || "refresh_failed";
      toast.error(t("connection_detail.refresh_failed",
                          "OAuth-Refresh fehlgeschlagen."));
      setBanner({ kind: "refresh", ok: false,
                     message: String(reason) });
    },
  });

  const sync = useMutation({
    mutationFn: async () => {
      const r = await api.post(`/integrations/crm/connections/${id}/sync`);
      return r.data;
    },
    onSuccess: (r) => setBanner({ kind: "sync", ok: true,
        message: r.message || JSON.stringify(r) }),
    onError: (e) => setBanner({ kind: "sync", ok: false,
        message: e?.response?.data?.detail || "sync_failed" }),
  });

  // ---- OAuth expiry countdown (re-renders every 30s) ----
  // Hooks must be called unconditionally — these run before any early return.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const tid = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(tid);
  }, []);

  if (isLoading) {
    return <div className="p-6 text-sm text-muted-foreground"
                data-testid="connection-loading">
      {t("common.loading", "Lade…")}
    </div>;
  }
  if (error || !data) {
    return <div className="p-6 text-rose-600 text-sm"
                data-testid="connection-error">
      {t("connection_detail.error", "Verbindung nicht gefunden.")}
    </div>;
  }

  const conn = data;
  const currentName = edit.name ?? conn.name;
  const currentStatus = edit.status ?? conn.status;
  const currentConfig = edit.config ?? JSON.stringify(conn.config || {}, null, 2);

  const expiresAt = conn.oauth_tokens?.expires_at || null;
  const countdown = (() => {
    if (!expiresAt) return null;
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (Number.isNaN(ms)) return null;
    if (ms <= 0) return { label: t("connection_detail.token_expired", "abgelaufen"),
                             expired: true, soon: true };
    const totalMin = Math.floor(ms / 60_000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    const label = h > 0 ? `${h}h ${m}m` : `${m}m`;
    return { label, expired: false, soon: ms < 24 * 3600 * 1000 };
  })();

  const saveBasics = () => {
    let parsedConfig;
    try { parsedConfig = JSON.parse(currentConfig); }
    catch (e) { setConfigError(t("connection_detail.invalid_json",
        "Ungültiges JSON in 'config'.")); return; }
    setConfigError(null);
    patch.mutate({ name: currentName, status: currentStatus,
                      config: parsedConfig });
  };

  return (
    <div className="p-6 space-y-6" data-testid="connection-editor-page">
      <div>
        <Link to="/app/integrations"
              className="inline-flex items-center text-sm text-muted-foreground hover:underline"
              data-testid="connection-back">
          <ArrowLeft className="h-4 w-4 mr-1" />
          {t("nav.integrations", "Integrationen")}
        </Link>
        <h1 className="mt-1 text-2xl font-bold tracking-tight inline-flex items-center gap-2">
          <Plug className="h-6 w-6" />
          <span data-testid="connection-name">{conn.name}</span>
        </h1>
        <div className="mt-2 flex items-center gap-2">
          <Badge variant="secondary"
                 data-testid="connection-kind">{conn.connector_kind}</Badge>
          <StatusBadge status={conn.status} />
          <AuditHistoryLink kind="crm_connection" id={conn.id}
                            testid="connection-audit-history-link" />
        </div>
      </div>

      {banner && (
        <div className={`text-sm rounded p-3 ${banner.ok
            ? "bg-emerald-50 text-emerald-800"
            : "bg-rose-50 text-rose-800"}`}
             data-testid="connection-action-banner">
          [{banner.kind}] {banner.message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Read-only summary */}
        <Card data-testid="connection-summary-card">
          <CardHeader><CardTitle>{t("connection_detail.summary", "Überblick")}</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div><span className="text-muted-foreground">created_at:</span> {conn.created_at}</div>
            <div><span className="text-muted-foreground">updated_at:</span> {conn.updated_at}</div>
            <div><span className="text-muted-foreground">last_sync_at:</span> {conn.last_sync_at || "—"}</div>
            <div><span className="text-muted-foreground">last_error:</span> {conn.last_error || "—"}</div>
            <div data-testid="connection-oauth-configured"
                 className="flex items-center gap-2 flex-wrap">
              <span className="text-muted-foreground">oauth_configured:</span>
              <Badge variant={conn.oauth_configured ? "default" : "secondary"}>
                {String(conn.oauth_configured)}
              </Badge>
              {expiresAt && (
                <span data-testid="oauth-expires-row"
                      className="inline-flex items-center gap-1 ml-1">
                  <Clock className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">expires_at:</span>
                  <Badge
                    variant="outline"
                    data-testid={countdown?.expired
                        ? "oauth-countdown-expired"
                        : (countdown?.soon
                            ? "oauth-countdown-soon"
                            : "oauth-countdown-ok")}
                    className={
                      countdown?.expired ? "bg-rose-100 text-rose-800 border-rose-200"
                      : countdown?.soon ? "bg-amber-100 text-amber-800 border-amber-200"
                      : "bg-emerald-50 text-emerald-700 border-emerald-200"}
                  >
                    {countdown ? countdown.label : "—"}
                  </Badge>
                </span>
              )}
            </div>
            <div data-testid="connection-webhook-configured">
              <span className="text-muted-foreground">webhook_secret_configured:</span>{" "}
              <Badge variant={conn.webhook_secret_configured ? "default" : "secondary"}>
                {String(conn.webhook_secret_configured)}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Edit form */}
        <Card data-testid="connection-edit-card">
          <CardHeader><CardTitle>{t("connection_detail.edit", "Bearbeiten")}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>{t("connection_detail.name", "Name")}</Label>
              <Input value={currentName}
                     onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))}
                     data-testid="connection-name-input" />
            </div>
            <div>
              <Label>{t("connection_detail.status", "Status")}</Label>
              <Select value={currentStatus}
                      onValueChange={(v) => setEdit((p) => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="connection-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="connected">connected</SelectItem>
                  <SelectItem value="disconnected">disconnected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>config (JSON)</Label>
              <Textarea value={currentConfig} rows={6}
                        className="font-mono text-xs"
                        onChange={(e) => setEdit((p) => ({ ...p, config: e.target.value }))}
                        data-testid="connection-config-textarea" />
              {configError && (
                <div className="text-xs text-rose-600 mt-1"
                     data-testid="connection-config-error">{configError}</div>
              )}
            </div>
            <Button onClick={saveBasics} disabled={patch.isPending}
                    data-testid="connection-save-btn">
              {patch.isPending ? t("common.saving", "Speichern…")
                              : t("common.save", "Speichern")}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Secrets + actions */}
      <Card data-testid="connection-secrets-card">
        <CardHeader><CardTitle>
          {t("connection_detail.secrets_actions", "Secrets & Aktionen")}
        </CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setOauthOpen(true)}
                  data-testid="connection-rotate-oauth-btn">
            <KeyRound className="h-4 w-4 mr-1" />
            {t("connection_detail.rotate_oauth", "OAuth rotieren")}
          </Button>
          <Button
            variant={countdown?.expired ? "default" : "outline"}
            size="sm"
            onClick={() => refreshTokens.mutate()}
            disabled={refreshTokens.isPending}
            data-testid="connection-refresh-tokens-btn"
          >
            <RefreshCw
              className={`h-4 w-4 mr-1
                              ${refreshTokens.isPending ? "animate-spin" : ""}`}
            />
            {refreshTokens.isPending
              ? t("connection_detail.refreshing", "Aktualisiere…")
              : t("connection_detail.refresh_now", "Jetzt aktualisieren")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSecretOpen(true)}
                  data-testid="connection-rotate-secret-btn">
            <Webhook className="h-4 w-4 mr-1" />
            {t("connection_detail.rotate_secret", "Secret rotieren")}
          </Button>
          <Button variant="outline" size="sm"
                  onClick={() => test.mutate()}
                  disabled={test.isPending}
                  data-testid="connection-test-btn">
            <FlaskConical className="h-4 w-4 mr-1" />
            {t("connection_detail.test", "Test")}
          </Button>
          <Button variant="outline" size="sm"
                  onClick={() => sync.mutate()}
                  disabled={sync.isPending}
                  data-testid="connection-sync-btn">
            <RefreshCw className="h-4 w-4 mr-1" />
            {t("connection_detail.sync_now", "Jetzt synchronisieren")}
          </Button>
        </CardContent>
      </Card>

      <OAuthRotateDialog
        open={oauthOpen} onClose={() => setOauthOpen(false)}
        onSubmit={(tokens) => {
          patch.mutate({ oauth_tokens: tokens });
          setOauthOpen(false);
        }} />
      <SecretRotateDialog
        open={secretOpen} onClose={() => setSecretOpen(false)}
        onSubmit={(s) => {
          patch.mutate({ webhook_secret: s });
          setSecretOpen(false);
        }} />
    </div>
  );
}
